import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { chatbotApi, statisticsApi } from '../services/api';
import { clsx } from 'clsx';
import { toast } from 'react-hot-toast';

interface SessionData {
  session_id: string;
  chatbot_id: string;
  platform: string;
  created_at: number;
  last_interaction: number;
  total_messages: number;
  duration_seconds: number;
  status: string;
}

interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_sessions: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

interface ConversationMessage {
  message_type: string;
  content: string;
  timestamp: string;
  message_sequence: number;
  message_length: number;
  model_id?: string;
  response_time_ms?: number;
}

interface SessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatbotId: string;
  initialStartDate?: string;
  initialEndDate?: string;
}

export default function SessionsModal({ 
  isOpen, 
  onClose, 
  chatbotId, 
  initialStartDate, 
  initialEndDate 
}: SessionsModalProps) {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [conversationData, setConversationData] = useState<Record<string, ConversationMessage[]>>({});

  useEffect(() => {
    if (isOpen) {
      loadSessions(1);
    }
  }, [isOpen, chatbotId, initialStartDate, initialEndDate]);

  const loadSessions = async (page: number) => {
    try {
      setLoading(true);
      console.log('Loading sessions with params:', {
        chatbotId,
        page,
        limit: 20,
        startDate: initialStartDate,
        endDate: initialEndDate
      });
      
      const response = await chatbotApi.getChatbotSessionsPaginated(
        chatbotId,
        page,
        20,
        initialStartDate,
        initialEndDate
      );

      console.log('Sessions response:', response);

      if (response.success && response.data) {
        setSessions(response.data.sessions);
        setPagination(response.data.pagination);
        setCurrentPage(page);
      } else {
        console.error('Failed response:', response);
        toast.error('Failed to load sessions');
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (pagination?.total_pages || 1)) {
      loadSessions(page);
    }
  };

  const toggleSessionExpansion = (sessionId: string) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
      // Load conversation data if not already loaded
      if (!conversationData[sessionId]) {
        loadConversationData(sessionId);
      }
    }
    setExpandedSessions(newExpanded);
  };

  const loadConversationData = async (sessionId: string) => {
    try {
      const response = await statisticsApi.getConversationData(sessionId);
      if (response.success && response.data) {
        setConversationData(prev => {
          const newData = { ...prev };
          newData[sessionId] = response.data!;
          return newData;
        });
      } else {
        toast.error('Failed to load conversation data');
      }
    } catch (error) {
      console.error('Error loading conversation data:', error);
      toast.error('Failed to load conversation data');
    }
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const renderPaginationNumbers = () => {
    if (!pagination) return null;

    const { current_page, total_pages } = pagination;
    const pages = [];
    
    // Always show first page
    if (total_pages > 0) pages.push(1);
    
    // Show pages around current page
    const start = Math.max(2, current_page - 1);
    const end = Math.min(total_pages - 1, current_page + 1);
    
    // Add ellipsis if needed
    if (start > 2) pages.push('...');
    
    // Add middle pages
    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== total_pages) pages.push(i);
    }
    
    // Add ellipsis if needed
    if (end < total_pages - 1) pages.push('...');
    
    // Always show last page
    if (total_pages > 1) pages.push(total_pages);
    
    return pages.map((page, index) => (
      <button
        key={index}
        onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
        disabled={page === '...' || page === current_page}
        className={clsx(
          'px-3 py-2 text-sm font-medium rounded-md',
          page === current_page
            ? 'bg-primary-600 text-white'
            : page === '...'
            ? 'text-gray-400 cursor-default'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
        )}
      >
        {page}
      </button>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                All Sessions
              </h3>
              {pagination && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {pagination.total_sessions} total sessions
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Sessions List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">No sessions found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-600">
                {sessions.map((session) => (
                  <div key={session.session_id}>
                    {/* Session Header */}
                    <div 
                      className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => toggleSessionExpansion(session.session_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={clsx(
                            'h-2 w-2 rounded-full',
                            session.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                          )}></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100" title={session.session_id}>
                              {session.session_id.slice(0, 80)}...
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {session.total_messages} messages • {formatDuration(session.duration_seconds)} • {session.platform}
                            </p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTime(session.created_at)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Conversation Messages — kept identical to the expanded
                        session on the Stats tab, which this modal is the
                        "show all" version of. Both now render the Test tab's
                        flat transcript rather than bubbles. */}
                    {expandedSessions.has(session.session_id) && (
                      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                        <div className="space-y-4 max-h-64 overflow-y-auto">
                          {conversationData[session.session_id] ? (
                            conversationData[session.session_id].map((message, index) => (
                              <div key={index} className="flex justify-start">
                                <div className="max-w-[90%] py-2">
                                  <p className={clsx(
                                    'text-xs mb-1',
                                    message.message_type === 'user'
                                      ? 'text-primary-700 dark:text-primary-400 font-semibold'
                                      : 'text-gray-400 dark:text-gray-500'
                                  )}>
                                    {message.message_type === 'user' ? 'You' : 'Assistant'} •{' '}
                                    {new Date(parseInt(message.timestamp)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                  <p className={clsx(
                                    'text-sm leading-relaxed whitespace-pre-wrap',
                                    message.message_type === 'user'
                                      ? 'text-primary-900 dark:text-primary-300'
                                      : 'text-gray-900 dark:text-gray-100'
                                  )}>
                                    {message.content}
                                  </p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((currentPage - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(currentPage * pagination.per_page, pagination.total_sessions)} of{' '}
                  {pagination.total_sessions} sessions
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.has_prev}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                  
                  <div className="flex space-x-1">
                    {renderPaginationNumbers()}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.has_next}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}